import { Button } from '@heroui/react';
import type { AvailableSpot } from '../../api/available-spots';
import {
  chatSpotKind,
  formatChatPeriod,
  formatChatSpotPlace,
} from './assistant-spot-format';

interface AssistantSpotCardsProps {
  spots: AvailableSpot[];
  onReserve: (spot: AvailableSpot) => void;
  embedded?: boolean;
}

const KIND_CLASS = {
  own: 'bg-accent/15 text-accent',
  rent: 'bg-warning/15 text-warning',
  borrow: 'bg-success/15 text-success',
} as const;

export function AssistantSpotCards({
  spots,
  onReserve,
  embedded = false,
}: AssistantSpotCardsProps) {
  const bookable = spots.filter((spot) => !spot.isMine);
  if (bookable.length === 0) return null;

  return (
    <div
      className={`flex flex-col gap-2 ${embedded ? 'w-full' : 'w-full max-w-[90%] self-start'}`}
    >
      {bookable.map((spot) => {
        const kind = chatSpotKind(spot);
        const place = formatChatSpotPlace(spot);
        const windows = spot.availabilityWindows.slice(0, 3);
        const extra = spot.availabilityWindows.length - windows.length;

        return (
          <article
            key={spot.id}
            className={`rounded-2xl border border-separator px-3 py-2.5 ${
              embedded ? 'bg-surface' : 'bg-surface shadow-sm'
            }`}
          >
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="text-[15px] font-semibold leading-tight tracking-tight">
                  {spot.code}
                </p>
                {place ? (
                  <p className="mt-0.5 truncate text-xs text-muted">{place}</p>
                ) : null}
              </div>
              <span
                className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium ${KIND_CLASS[kind.tone]}`}
              >
                {kind.label}
              </span>
            </div>

            {windows.length > 0 ? (
              <ul className="mt-2 flex flex-col gap-1">
                {windows.map((window) => (
                  <li
                    key={window.id}
                    className="text-[13px] font-medium tabular-nums leading-snug text-foreground"
                  >
                    {formatChatPeriod(window.startAt, window.endAt)}
                  </li>
                ))}
                {extra > 0 ? (
                  <li className="text-xs text-muted">+{extra} período(s)</li>
                ) : null}
              </ul>
            ) : null}

            {spot.isMine ? null : (
              <Button
                size="sm"
                variant="primary"
                className="mt-2.5 h-8 w-full"
                onPress={() => onReserve(spot)}
              >
                Reservar
              </Button>
            )}
          </article>
        );
      })}
    </div>
  );
}
